import React from "react";
import ServiceHeroSection from "../common/service-hero-section";
import { AiBlog } from "@/components/main";
import {
  FeatureGrid,
  ProcessSteps,
  RelatedServices,
  ScenarioGrid,
  ServiceAudience,
  ServiceBottomCta,
  ServiceFaq,
  ServiceSection,
  ServiceSectionHeader,
  SplitContent,
} from "../common/service-content-blocks";

const CAPABILITIES = [
  {
    title: "Problem framing",
    description:
      "Turn a vague AI idea into a scoped workflow, success metric, and risk list.",
  },
  {
    title: "Data and integration check",
    description:
      "Test whether the data and systems needed actually support the use case.",
  },
  {
    title: "Focused build",
    description:
      "Ship a narrow vertical slice users can try, not a slide deck of architecture options.",
  },
  {
    title: "Evaluation with operators",
    description:
      "Measure quality with the people who will live with the result.",
  },
  {
    title: "Go or no-go recommendation",
    description:
      "Clear evidence on what to scale, change, or stop before you fund a larger program.",
  },
  {
    title: "Path to production",
    description:
      "If the pilot works, we map the hardening work required to operate it.",
  },
];

const APPROACH = [
  {
    title: "One workflow, one metric",
    description:
      "Pilots fail when scope sprawls. We lock a single job and a measurable outcome.",
  },
  {
    title: "Real data early",
    description:
      "Synthetic demos hide risk. We push for representative samples as soon as possible.",
  },
  {
    title: "Honest constraints",
    description:
      "Access, latency, and review requirements are part of the design, not footnotes.",
  },
  {
    title: "Decision-ready output",
    description:
      "You leave with evidence and a recommendation, not an endless experiment.",
  },
];

const SCENARIOS = [
  {
    title: "Agent feasibility pilot",
    description:
      "Prove whether an agent can complete a multi-step ops task with your tools and review rules.",
    outcome: "Evidence on tool reliability and exception rates.",
  },
  {
    title: "Knowledge assistant pilot",
    description:
      "Test grounded Q&A on a defined corpus with citation and refusal checks.",
    outcome: "Quality baseline before broader rollout.",
  },
  {
    title: "Document automation pilot",
    description:
      "Run extraction and routing on a live sample set with human exception handling.",
    outcome: "Accuracy and throughput numbers leadership can trust.",
  },
  {
    title: "Integration spike",
    description:
      "Validate that critical systems can be read and written safely for the target workflow.",
    outcome: "Clear integration risk before a full build.",
  },
];

const DELIVERY = [
  {
    title: "Scope",
    description: "Lock workflow, users, data, and success criteria.",
  },
  {
    title: "Build",
    description: "Implement the narrow slice with evaluation hooks.",
  },
  {
    title: "Test",
    description: "Run with operators and capture failure modes.",
  },
  {
    title: "Decide",
    description: "Recommend scale, pivot, or stop with a production path if green.",
  },
];

export const FAQS = [
  {
    question: "How long is a typical pilot?",
    answer:
      "Many land in a few weeks when scope is tight and access is ready. We set the timeline after discovery.",
  },
  {
    question: "What do we get at the end?",
    answer:
      "A working slice, evaluation results, risks, and a written recommendation for next steps.",
  },
  {
    question: "Can a pilot become the production system?",
    answer:
      "Sometimes. We design pilots so successful pieces can harden into production rather than be thrown away.",
  },
];

function MvpPocDevelopmentComponent({ posts = [] }) {
  return (
    <>
      <ServiceHeroSection
        headlinePrimary="Prove value"
        headlineHighWhite="before you scale spend"
        descriptionPrimary="Focused pilots that test workflow fit, data readiness, and integration risk with real users."
        descriptionSecondary="You get evidence and a clear go or no-go, not an open-ended experiment."
      />

      <ServiceAudience
        forItems={[
          "You have a promising use case but need proof before a full budget",
          "Stakeholders need evidence, not another strategy deck",
          "You can provide sample data and operator time",
          "You want a decision in weeks, not quarters",
        ]}
        notForItems={[
          "You already know the solution and only need production build",
          "There is no owner for the workflow under test",
          "Access to data or systems cannot be arranged",
          "Success criteria cannot be defined",
        ]}
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="What a Nivalabs pilot includes"
          lead="Enough build to learn, enough rigor to decide."
        />
        <FeatureGrid items={CAPABILITIES} />
      </ServiceSection>

      <ServiceSection muted>
        <SplitContent
          title="How we approach pilots"
          lead="Discipline that keeps PoCs from becoming science projects."
          points={APPROACH}
        />
      </ServiceSection>

      <ServiceSection>
        <ServiceSectionHeader
          title="Pilot shapes we run often"
        />
        <ScenarioGrid items={SCENARIOS} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader title="How we deliver" />
        <ProcessSteps steps={DELIVERY} />
      </ServiceSection>

      <ServiceFaq items={FAQS} />
      <ServiceBottomCta
        title="Have a use case to prove?"
        description="Tell us the workflow and what a successful pilot would change. We will propose a tight scope."
      />
      <RelatedServices
        items={[
          { href: "/ai-agent-development", label: "AI agents" },
          { href: "/custom-llm-application-development", label: "LLM applications" },
          { href: "/ai-workflow-automation", label: "Workflow automation" },
          { href: "/ai-integrations", label: "Integrations" },
        ]}
      />
      <AiBlog posts={posts} />
    </>
  );
}

export default MvpPocDevelopmentComponent;
